import { useMutation } from '@apollo/client';
import { AllTimelineGqlOptions, useTimeline } from 'components/events/Timeline/TimelineContext';
import { getSectionCache } from 'graphql/cache/sections';
import { onError } from 'graphql/helpers';
import { debounce, pick } from 'lodash';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const useOpenSection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const openSection = (id: string) => {
    const { pathname } = location;

    navigate({
      search: location.search,
      pathname: pathname.replace(/timeline(\/\w*)?/, `timeline/${id}`),
    });
  };

  return {
    openSection,
  };
};

export interface UpdateSectionDebounce<T> {
  payload: Partial<T>;
  sectionId: string;
}

export const useUpdateSectionDebounce = <T extends {}>() => {
  const {
    variables,
    sections: { mutations, fragments },
  } = useTimeline<AllTimelineGqlOptions>();

  const [updateSection] = useMutation(mutations.updateSection.mutation, {
    onError,
  });

  const updateSectionCache = useCallback(
    debounce((sectionId: string, payload: Partial<TemplateSection>) => {
      mutations.updateSection.updateCache?.(sectionId, payload);
    }, 150),
    []
  );
  const updateSectionNetwork = useCallback(
    debounce((sectionId: string, payload: Partial<TemplateSection>) => {
      const cache = !!fragments?.section
        ? pick(getSectionCache(sectionId, fragments.section), ['name', 'time'])
        : null;

      updateSection({
        variables: {
          ...variables,
          sectionId: sectionId,
          favoriteSectionId: sectionId,
          payload: {
            ...cache,
            ...payload,
          },
        },
      });
    }, 2000),
    []
  );
  const handleUpdateSection = ({ sectionId, payload }: UpdateSectionDebounce<T>) => {
    updateSectionCache(sectionId, payload);
    updateSectionNetwork(sectionId, payload);
  };

  return { handleUpdateSection };
};
